// ---------------------------------------------------------------------------
// AccountHealthZone — Zone 1 on the Channels page.
// ---------------------------------------------------------------------------
// Renders a collapsible "health-at-a-glance" bar for the authenticated user's
// account. Default is collapsed. Auto-expands (and locks open) when
// healthState ∈ { 'approaching', 'at_cap' }.
//
// Data comes from the /api/users/me endpoint via UserProfileContext.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Heart, Info, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUserProfile } from '@/hooks/useUserProfile';
import type { AccountHealth, HealthState } from '@/services/userService';

const STORAGE_KEY = 'accountHealth.collapsed';

// ─── Public API ─────────────────────────────────────────────────────────────

export interface AccountHealthZoneProps {
  /** Optional override for the URL-bound `data-testid` parent prefix. */
  readonly testIdPrefix?: string;
}

export function AccountHealthZone({ testIdPrefix = 'account-health' }: AccountHealthZoneProps) {
  const { userData, isLoading, error } = useUserProfile();
  const data = userData.accountHealth ?? null;
  const isDemo = userData.isDemo ?? false;

  const [userCollapsed, setUserCollapsed] = useState<boolean>(() => readCollapsedPref());

  // Auto-expand + lock when not healthy.
  const isLocked = data ? data.healthState !== 'healthy' : false;
  const isCollapsed = !isLocked && userCollapsed;

  const toggle = useCallback(() => {
    if (isLocked) return;
    setUserCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* ignore — storage may be blocked */
      }
      return next;
    });
  }, [isLocked]);

  // When leaving locked state, honour the user's stored preference as-is.
  useEffect(() => {
    if (!data) return;
    if (isLocked) return;
    // No-op: pref is already in state; kept here for future extension.
  }, [data, isLocked]);

  if (isLoading) {
    return <SkeletonBar testId={`${testIdPrefix}-loading`} />;
  }

  if (error || !data) {
    if (!data && !error) {
      // accountHealth is null — no active token period
      return null;
    }
    return (
      <ErrorBar
        message={error ?? 'Failed to load account health'}
        testId={`${testIdPrefix}-error`}
      />
    );
  }

  return (
    <div data-testid={testIdPrefix} className="flex flex-col gap-3">
      {isLocked && <WarningBanner state={data.healthState} data={data} />}
      {isCollapsed ? (
        <CollapsedBar data={data} onExpand={toggle} testId={`${testIdPrefix}-collapsed`} isDemo={isDemo} />
      ) : (
        <ExpandedView
          data={data}
          isLocked={isLocked}
          onCollapse={toggle}
          testId={`${testIdPrefix}-expanded`}
          isDemo={isDemo}
        />
      )}
    </div>
  );
}

// ─── Collapsed Bar (48px) ───────────────────────────────────────────────────

interface CollapsedBarProps {
  readonly data: AccountHealth;
  readonly onExpand: () => void;
  readonly testId: string;
  readonly isDemo: boolean;
}

function CollapsedBar({ data, onExpand, testId, isDemo }: CollapsedBarProps) {
  const percent = toPercent(data.tokensUsed, data.tokensLimit);
  const style = stateStyle(data.healthState);

  return (
    <button
      type="button"
      onClick={onExpand}
      data-testid={testId}
      className={cn(
        'flex h-12 w-full items-center gap-4 rounded-xl border px-4 transition-all',
        'bg-v11-surface border-v11-border hover:border-v11-border',
        'focus:outline-none focus:ring-2 focus:ring-cyan-400/40'
      )}
      aria-label={`Account Health (${data.healthState}, ${percent}% used). Click to expand.`}
    >
      <span className="flex items-center gap-2 shrink-0">
        <Heart className="h-3.5 w-3.5" style={{ color: '#22D3EE' }} aria-hidden />
        <span className="text-[11px] font-semibold tracking-[0.08em] text-v11-text-primary">
          ACCOUNT HEALTH
        </span>
      </span>

      <span className="relative h-1.5 flex-1 min-w-0 overflow-hidden rounded-full bg-v11-bg-secondary">
        <span
          data-testid={`${testId}-progress`}
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ width: `${Math.min(percent, 100)}%`, background: style.fill }}
        />
      </span>

      <span className="flex items-baseline gap-1.5 shrink-0 tabular-nums">
        <span className="text-sm font-semibold text-v11-text-primary">
          {formatNumber(data.tokensUsed)}
        </span>
        <span className="text-[11px] text-v11-text-muted">/ {formatCompact(data.tokensLimit)}</span>
      </span>

      <span
        data-testid={`${testId}-badge`}
        className="flex items-center gap-1.5 rounded-full border px-2 py-0.5 shrink-0"
        style={{ background: style.pillBg, borderColor: style.pillBorder }}
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: style.fill }} />
        <span className="text-[10px] font-semibold tracking-wide" style={{ color: style.text }}>
          {percent}%
        </span>
      </span>

      {isDemo && <DemoBadge />}

      <span className="text-[10px] text-v11-text-muted shrink-0">
        as of {formatUtcTime(data.tokensUsedUpdatedAt)}
      </span>

      <ChevronDown className="h-2.5 w-2.5 text-v11-text-secondary shrink-0" aria-hidden />
    </button>
  );
}

// ─── Expanded View ──────────────────────────────────────────────────────────

interface ExpandedViewProps {
  readonly data: AccountHealth;
  readonly isLocked: boolean;
  readonly onCollapse: () => void;
  readonly testId: string;
  readonly isDemo: boolean;
}

function ExpandedView({ data, isLocked, onCollapse, testId, isDemo }: ExpandedViewProps) {
  const percent = toPercent(data.tokensUsed, data.tokensLimit);
  const style = stateStyle(data.healthState);

  return (
    <section
      data-testid={testId}
      className="flex flex-col gap-4 rounded-2xl border border-v11-border bg-v11-surface p-6"
    >
      <header className="flex items-center justify-between">
        <span className="flex items-center gap-2">
          <Heart className="h-4 w-4" style={{ color: '#22D3EE' }} aria-hidden />
          <span className="text-[13px] font-semibold tracking-[0.02em] text-v11-text-primary">
            ACCOUNT HEALTH
          </span>
        </span>
        {isLocked ? (
          <span
            data-testid={`${testId}-lock`}
            className="flex items-center gap-2 rounded-md border px-3 py-1"
            style={{ background: style.pillBg, borderColor: style.pillBorder }}
          >
            <Lock className="h-2.5 w-2.5" style={{ color: style.text }} aria-hidden />
            <span className="text-[10px] font-semibold tracking-wide" style={{ color: style.text }}>
              {data.healthState === 'at_cap'
                ? 'SECTION LOCKED UNTIL NEXT QUOTA CYCLE'
                : 'VISIBLE UNTIL USAGE RETURNS BELOW 80%'}
            </span>
          </span>
        ) : (
          <button
            type="button"
            onClick={onCollapse}
            data-testid={`${testId}-collapse`}
            className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-v11-surface-hover"
            aria-label="Collapse Account Health"
          >
            <ChevronUp className="h-3 w-3 text-v11-text-secondary" aria-hidden />
          </button>
        )}
      </header>

      {/* Token widget — flat inner surface */}
      <div className="flex flex-col gap-3 rounded-xl border border-v11-border bg-v11-surface p-5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <span className="text-[11px] font-medium tracking-wide text-v11-text-secondary">
              TOKEN QUOTA
            </span>
            <span title="1 token = 1 comment analysed">
              <Info className="h-3 w-3 text-v11-text-muted" aria-hidden />
            </span>
          </span>
          <span className="flex items-center gap-3">
            {isDemo && <DemoBadge />}
            <span className="flex items-center gap-1 text-[11px] text-v11-text-muted">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Updated as of {formatUtcTime(data.tokensUsedUpdatedAt)}
            </span>
          </span>
        </div>

        <div className="flex items-baseline gap-3">
          <span className="text-[44px] font-semibold leading-none tracking-tight text-v11-text-primary tabular-nums">
            {formatNumber(data.tokensUsed)}
          </span>
          <span className="text-xl text-v11-text-muted tabular-nums">
            / {formatNumber(data.tokensLimit)}
          </span>
          <span
            data-testid={`${testId}-state-pill`}
            className="ml-auto flex items-center gap-1.5 rounded-full border px-2.5 py-1"
            style={{ background: style.pillBg, borderColor: style.pillBorder }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: style.fill }} />
            <span className="text-[11px] font-semibold tracking-wide" style={{ color: style.text }}>
              {style.label} · {percent}%
            </span>
          </span>
        </div>

        <div className="relative h-2 w-full overflow-hidden rounded-full bg-v11-bg-secondary">
          <div
            data-testid={`${testId}-progress`}
            className="absolute inset-y-0 left-0 rounded-full"
            style={{ width: `${Math.min(percent, 100)}%`, background: style.fill }}
          />
        </div>

        <div className="flex items-center justify-between text-xs text-v11-text-muted">
          <span>
            Resets on{' '}
            <span className="font-medium text-v11-text-secondary">
              {formatDate(data.periodEndDate)}
            </span>
            {' · started '}
            {formatDate(data.periodStartDate)}
          </span>
          <span>
            At current pace, projected{' '}
            <span className="font-medium text-v11-text-secondary">
              {data.projectedEndPercentage}% by period end
            </span>
          </span>
        </div>
      </div>

      {/* Lifetime KPIs */}
      <div className="flex flex-col gap-2">
        <span className="text-[11px] font-medium tracking-[0.08em] text-v11-text-muted">
          LIFETIME TOTALS SINCE ACCOUNT CREATION · {formatDate(data.accountCreatedAt)}
        </span>
        <div className="grid grid-cols-4 gap-3">
          <KpiTile
            label="COMMENTS ANALYSED"
            value={data.lifetimeTotals.commentsAnalysed}
            caption="= tokens consumed (lifetime)"
          />
          <KpiTile
            label="POSTS ANALYSED"
            value={data.lifetimeTotals.postsAnalysed}
            caption="passive capture · no token cost"
          />
          <KpiTile
            label="PROFILES CAPTURED"
            value={data.lifetimeTotals.profilesCaptured}
            caption="unique authors identified"
          />
          <KpiTile
            label="PROFILES PROCESSED"
            value={data.lifetimeTotals.profilesProcessed}
            caption="AI-enriched (gender, age, lang)"
          />
        </div>
      </div>
    </section>
  );
}

// ─── Warning Banner ─────────────────────────────────────────────────────────

function WarningBanner({
  state,
  data,
}: {
  readonly state: HealthState;
  readonly data: AccountHealth;
}) {
  const remaining = Math.max(data.tokensLimit - data.tokensUsed, 0);
  const style = stateStyle(state);

  if (state === 'approaching') {
    return (
      <div
        data-testid="account-health-banner-approaching"
        className="flex items-center gap-3 rounded-xl border p-3.5"
        style={{ background: 'rgba(245,158,11,0.12)', borderColor: 'rgba(245,158,11,0.3)' }}
      >
        <AlertTriangle className="h-5 w-5 shrink-0" style={{ color: style.fill }} aria-hidden />
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-[13px] font-semibold" style={{ color: style.fill }}>
            Approaching annual token limit — {formatNumber(remaining)} tokens remaining
          </span>
          <span className="text-xs text-v11-text-secondary">
            Scraping stops automatically at 100%.
          </span>
        </div>
      </div>
    );
  }

  if (state === 'at_cap') {
    return (
      <div
        data-testid="account-health-banner-at-cap"
        className="flex items-center gap-3 rounded-xl border p-4"
        style={{ background: 'rgba(239,68,68,0.18)', borderColor: 'rgba(239,68,68,0.4)' }}
      >
        <Lock className="h-5 w-5 shrink-0" style={{ color: style.fill }} aria-hidden />
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-sm font-semibold" style={{ color: style.fill }}>
            Period Token Limit reached — scraping paused across all channels
          </span>
          <span className="text-xs text-v11-text-secondary">
            Contact support to upgrade, or wait until {formatDate(data.periodEndDate)} for quota
            renewal. Historical data remains accessible.
          </span>
        </div>
      </div>
    );
  }

  return null;
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function KpiTile({
  label,
  value,
  caption,
}: {
  readonly label: string;
  readonly value: number;
  readonly caption: string;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-v11-border bg-v11-surface px-5 py-4">
      <span className="text-[11px] font-medium tracking-[0.04em] text-v11-text-secondary">
        {label}
      </span>
      <span className="text-[28px] font-semibold leading-none text-v11-text-primary tabular-nums">
        {formatCompact(value)}
      </span>
      <span className="text-[11px] text-v11-text-muted">{caption}</span>
    </div>
  );
}

function DemoBadge() {
  return (
    <span
      data-testid="account-health-demo-badge"
      title="Token values are placeholder data while the real infrastructure ships."
      className="flex items-center rounded border border-v11-border bg-v11-surface-hover px-1.5 py-0.5 text-[9px] font-semibold tracking-wider text-v11-text-secondary"
    >
      DEMO
    </span>
  );
}

function SkeletonBar({ testId }: { readonly testId: string }) {
  return (
    <div
      data-testid={testId}
      className="h-12 w-full animate-pulse rounded-xl border border-v11-border bg-v11-surface"
    />
  );
}

function ErrorBar({ message, testId }: { readonly message: string; readonly testId: string }) {
  return (
    <div
      data-testid={testId}
      role="alert"
      className="flex h-12 w-full items-center gap-3 rounded-xl border border-red-900/40 bg-red-950/20 px-4 text-xs text-red-400"
    >
      <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
      <span>{message}</span>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function readCollapsedPref(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) return true; // default collapsed
    return stored !== 'false';
  } catch {
    return true;
  }
}

function toPercent(used: number, limit: number): number {
  if (limit <= 0) return 100;
  return Math.min(Math.round((used / limit) * 100), 999);
}

interface StateStyle {
  readonly label: string;
  readonly fill: string;
  readonly text: string;
  readonly pillBg: string;
  readonly pillBorder: string;
}

function stateStyle(state: HealthState): StateStyle {
  switch (state) {
    case 'approaching':
      return {
        label: 'APPROACHING',
        fill: '#F59E0B',
        text: '#F59E0B',
        pillBg: 'rgba(245,158,11,0.12)',
        pillBorder: 'rgba(245,158,11,0.3)',
      };
    case 'at_cap':
      return {
        label: 'AT LIMIT',
        fill: '#EF4444',
        text: '#EF4444',
        pillBg: 'rgba(239,68,68,0.15)',
        pillBorder: 'rgba(239,68,68,0.35)',
      };
    case 'healthy':
    default:
      return {
        label: 'HEALTHY',
        fill: '#10B981',
        text: '#10B981',
        pillBg: 'rgba(16,185,129,0.12)',
        pillBorder: 'rgba(16,185,129,0.25)',
      };
  }
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
  return n.toLocaleString('en-US');
}

function formatUtcTime(iso: string): string {
  try {
    const d = new Date(iso);
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `${hh}:${mm} UTC`;
  } catch {
    return '—';
  }
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}
